import { differenceInDays } from 'date-fns';

/**
 * Catalogue of in-app tours.
 *
 * A tour is an overlay walkthrough of UI that is already on screen, offered
 * behind a help button that carries a dot until it has been clicked once. Two
 * types share the same engine:
 *
 * - `onboarding` — evergreen. The dot shows to anyone who has not clicked yet,
 *   and the tour stays available through the button forever after.
 * - `announcement` — tied to one released change. Its dot expires
 *   `ANNOUNCEMENT_WINDOW_DAYS` after `releasedAt`, so a user who arrives a year
 *   later is not flagged with last year's news. To re-announce something, add
 *   a new entry with a new `id` — seen-state is keyed by id, so everyone's dot
 *   lights again. There is deliberately no version field, because bumping one
 *   out of habit after a typo fix would re-flag every user in the product.
 *
 * A step's `target` names a `data-tour` attribute in the app. Targets are
 * resolved when the tour opens and may legitimately be absent — a sidebar is
 * unmounted when closed, not merely hidden — and a step whose target is missing
 * is dropped from that run, unless the step has a `prepare` stage that will
 * produce it. Anchoring it somewhere arbitrary instead reads as a bug, so a
 * tour shows only the steps it can actually point at.
 *
 * This file is pure data: copy, target names, and the *names* of the stages a
 * tour needs (`setup`, `prepare`). What a stage does is supplied by the adapter
 * that mounts `AppTour` beside the feature — see `AppTour` for the contract.
 * Nothing from the app may be imported here, and a test enforces it: that is
 * what keeps a fifth tour from turning this file into a hub that knows every
 * feature.
 *
 * Copy here is user-facing and product-approved — the steps and the `button`
 * that opens them, whose tooltip and label should say *which* tour it opens.
 * Treat it like UI text, not like comments — and keep it short. A step is glanced at, not read: long
 * descriptions get skipped wholesale, taking the short ones after them along
 * too. `MAX_DESCRIPTION_LENGTH` is enforced by the tests.
 *
 * ## Why a dot on a button, rather than opening by itself
 *
 * A tour that opens unbidden reaches everyone and interrupts everyone; one
 * hidden behind a plain button interrupts nobody and reaches almost nobody. The
 * dot sits between: it is noticed by most people who visit the view, costs a
 * glance, and goes away the first time they act on it. Nothing here opens over
 * a user's work.
 *
 * Whether the dot earns its keep is measurable. `AppTour` reports every start,
 * advance, dismissal and completion to the `tour` event on `dataLayer`; the
 * number to watch is starts against visits to the view. If almost nobody clicks
 * a badged button, the badge is not the fix — the copy or the placement is.
 *
 * Two caveats on reading that number. Cookiebot gates GTM, so users who decline
 * cookies are invisible and the counts undercount; and seen-state lives in
 * `localStorage`, so it measures browsers rather than people.
 */

export const TOUR_TYPE = Object.freeze({
	ONBOARDING: 'onboarding',
	ANNOUNCEMENT: 'announcement',
});

/**
 * The longest a step's description may be. Not a style preference: past roughly
 * this length a reader decides not to start.
 */
export const MAX_DESCRIPTION_LENGTH = 90;

/** How long an announcement stays interesting after its release date. */
export const ANNOUNCEMENT_WINDOW_DAYS = 60;

/** Tour ids, so consumers don't pass raw strings around. */
export const TOUR_ID = Object.freeze({
	COLLECTION_VIEW: 'collection-view',
	AI_DETECTION: 'ai-detection',
});

const COLLECTION_VIEW_TOUR = {
	id: TOUR_ID.COLLECTION_VIEW,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'Tour this view', label: 'Tour the collection view' },
	// Steps 3, 5 and 6 point into the Layers sidebar, which is unmounted when
	// closed; `ProjectTour` supplies the stage that opens it.
	setup: 'layerView',
	steps: [
		{
			target: 'collection-picker',
			title: 'Pick a collection',
			description: 'Choose a collection to view its detections on the map.',
			placement: 'bottom',
		},
		{
			target: 'ai-detection',
			title: 'Run a new detection',
			description: 'Pick a collection, choose an area, then run.',
			placement: 'bottom',
		},
		{
			target: 'layer-view',
			title: 'Everything drawn on the map',
			description: 'Turn layers on or off, reorder them, open their settings.',
			placement: 'right',
		},
		{
			target: 'map-legend',
			title: 'What the colors mean',
			description: 'Which color on the map belongs to which object.',
			placement: 'left',
		},
		{
			target: 'collection-objects',
			title: 'Filter and color by value',
			description: 'Filter detections by value, or recolor an object to spot a pattern.',
			placement: 'right',
		},
		{
			target: 'mode-switch',
			title: 'Model view is still here',
			description: 'The same project, analyzed by a trained model instead.',
			placement: 'right',
		},
	],
};

/**
 * Walks the detection wizard page by page. It lives inside the wizard, so it
 * never opens the wizard itself — each step names the page it needs and
 * `ObjectPickerTour` turns to it. It ends on the Run button rather than
 * pressing it: a detection run costs compute and changes the project, and a
 * tour must never start one. The progress dialog is described, not shown, for
 * the same reason.
 */
const AI_DETECTION_TOUR = {
	id: TOUR_ID.AI_DETECTION,
	type: TOUR_TYPE.ONBOARDING,
	button: { tooltip: 'How this wizard works', label: 'Tour the detection wizard' },
	steps: [
		{
			target: 'picker-collection',
			title: 'Choose a collection',
			description:
				'A saved set of objects to detect. Manage collections on the Collections page.',
			placement: 'top',
			prepare: 'collection',
		},
		{
			target: 'picker-area',
			title: 'Choose where to look',
			description: 'Draw an area on the map, or search the entire project.',
			placement: 'top',
			prepare: 'area',
		},
		{
			target: 'picker-run',
			title: 'Confirm and run',
			description: 'Starts the job. A progress dialog then follows it until results land.',
			placement: 'top',
			prepare: 'run',
		},
	],
};

export const TOURS = Object.freeze([COLLECTION_VIEW_TOUR, AI_DETECTION_TOUR]);

/** The tour with this id, or `undefined`. */
export const getTour = tourId => TOURS.find(tour => tour.id === tourId);

/**
 * Whether an announcement is still inside its window. Onboarding tours never
 * expire, so they always pass. An announcement missing `releasedAt` is treated
 * as expired rather than shown forever — a missing date is a mistake, and the
 * quiet failure is the safer one.
 */
export const isWithinWindow = (tour, now = Date.now()) => {
	if (tour?.type !== TOUR_TYPE.ANNOUNCEMENT) return true;
	if (!tour.releasedAt) return false;

	// An unparseable date gives NaN, and NaN compares false — expired.
	return differenceInDays(now, new Date(tour.releasedAt)) < ANNOUNCEMENT_WINDOW_DAYS;
};
